import { useState, useEffect } from "react";
import { C } from "../../../lib/tokens.js";
import { t } from "../../../lib/data.js";
import { useUser } from "../../../lib/UserCacheContext.jsx";
import { apiFetch } from "../../../lib/api.js";
import { useToast } from "../../../lib/ToastContext.jsx";
import { relTime, dateTimeFull, dateLong, isAgentRole } from "../../../lib/utils.js";
import { StatusChip, PriorityChip, TypeChip, ModuleChip, ClusterChip } from "../../../components/ui/Chips.jsx";
import { Avatar } from "../../../components/ui/Avatar.jsx";
import { ProgressBar } from "../../../components/ui/Misc.jsx";
import { Button } from "../../../components/ui/Button.jsx";
import { useExtensionSlot } from "../../../lib/ExtensionSlotsContext.jsx";
import { useLookups } from "../../../lib/LookupsContext.jsx";
import { findLookup } from "../../../components/ui/Chips.jsx";
import { billingMeta, useBillingType } from "../../../components/ui/BillingMarker.jsx";

function WatchersSection({ requestId, me }) {
  const { addToast } = useToast();
  const myId = me?.id;
  const [watchers, setWatchers] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiFetch(`/requests/${requestId}/watchers`)
      .then((data) => setWatchers(data.data ?? data ?? []))
      .catch(() => {});
  }, [requestId]);

  const amWatching = watchers.some((w) => w.id === myId || w.id === "me");

  async function toggleWatch() {
    setBusy(true);
    try {
      if (amWatching) {
        const watcherId = watchers.find((w) => w.id === myId || w.id === "me")?.id ?? myId;
        await apiFetch(`/requests/${requestId}/watchers/${encodeURIComponent(watcherId)}`, { method: "DELETE" });
        setWatchers((ws) => ws.filter((w) => w.id !== myId && w.id !== "me"));
      } else {
        await apiFetch(`/requests/${requestId}/watchers`, {
          method: "POST",
          body: JSON.stringify({ user_id: myId ?? "me" }),
        });
        // Real name/initials/avatar from `me`, not a placeholder - otherwise this
        // optimistic entry shows "You"/"?" until the next full reload replaces it
        // with the real fetched record.
        setWatchers((ws) => [...ws, {
          id: myId ?? "me", name: me?.name ?? "You", initials: me?.initials ?? "?",
          avatar_url: me?.avatar_url ?? null, kind: me?.role ?? "customer",
        }]);
      }
    } catch {
      addToast(amWatching ? "Could not unwatch this request." : "Could not watch this request.");
    } finally {
      setBusy(false);
    }
  }

  async function removeWatcher(w) {
    setBusy(true);
    try {
      await apiFetch(`/requests/${requestId}/watchers/${encodeURIComponent(w.id)}`, { method: "DELETE" });
      setWatchers((ws) => ws.filter((x) => x.id !== w.id));
    } catch {
      addToast("Could not remove watcher.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: 11, color: C.iron, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.04em" }}>
          Watchers
        </span>
        <button
          type="button"
          onClick={toggleWatch}
          disabled={busy}
          style={{
            display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px",
            border: `1px solid ${amWatching ? C.mercury : C.liquid}`, borderRadius: 3,
            background: amWatching ? C.platinum : C.rowActive,
            color: amWatching ? C.iron : C.deepsea, fontSize: 11,
            cursor: busy ? "default" : "pointer", fontFamily: "Roboto", fontWeight: 500,
          }}
        >
          <span className="material-icons" style={{ fontSize: 13 }}>
            {amWatching ? "notifications_off" : "notifications_none"}
          </span>
          {amWatching ? "Unwatch" : "Watch"}
        </button>
      </div>
      {watchers.length === 0 ? (
        <div style={{ fontSize: 12, color: C.silver, fontStyle: "italic" }}>No watchers yet.</div>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {watchers.map((w) => (
            <div
              key={w.id}
              style={{
                display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 6px 3px 4px",
                background: C.platinum, border: `1px solid ${C.mercury}`, borderRadius: 999, fontSize: 12,
              }}
            >
              <Avatar user={w} size={18} />
              <span style={{ color: C.fg }}>{w.name}</span>
              <button
                type="button"
                onClick={() => removeWatcher(w)}
                disabled={busy}
                style={{ border: "none", background: "none", cursor: "pointer", padding: 0, lineHeight: 1, color: C.silver, display: "flex" }}
              >
                <span className="material-icons" style={{ fontSize: 12 }}>close</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// One number input of the quote editor; the value is kept as text until the quote is saved.
function QuoteInput({ label, text, unit, onChange }) {
  return (
    <PanelRow label={label}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <input type="number" min="0" step="any" value={text} onChange={(e) => onChange(e.target.value)}
          style={{ width: 90, padding: "4px 8px", fontSize: 12, fontFamily: "Roboto", border: `1px solid ${C.mercury}`, borderRadius: 3, outline: "none", color: C.fg }} />
        <span style={{ fontSize: 11, color: C.iron }}>{unit}</span>
      </div>
    </PanelRow>
  );
}

// "12,5" / "12.5" / "" -> number / null; undefined when it is not a number.
function parseQuoteNumber(text) {
  const trimmed = text.trim();
  if (trimmed === "") return null;
  const num = Number(trimmed.replace(",", "."));
  return Number.isFinite(num) && num >= 0 ? num : undefined;
}

// Quote (expected hours / amount): shown for paid / sponsored types or once a quote exists.
// Read-only until an agent presses "Edit quote"; the button and the editor are only rendered
// for agents (same role check as the rest of the panel), everyone else just reads the quote.
function QuoteSection({ request, role, me, onUpdate, onRefresh }) {
  const { statuses } = useLookups() ?? {};
  const billingType = useBillingType(request);
  const meta = billingMeta(billingType);
  const isQuoteStatus = !!findLookup(statuses ?? [], request.status)?.is_quote;
  const hasQuote = request.qtyPlan != null || request.requestAmt != null;
  const isAgent = isAgentRole(role, me);
  const [editing, setEditing] = useState(false);
  const [hoursText, setHoursText] = useState("");
  const [amtText, setAmtText] = useState("");
  const [saving, setSaving] = useState(false);
  // The whole quote section is for agents only.
  if (!isAgent || !(billingType === "Q" || billingType === "S" || hasQuote)) return null;

  // Saving a quote can move the request on (waiting for quote -> new), so refresh afterwards.
  function startEdit() {
    setHoursText(request.qtyPlan == null ? "" : String(request.qtyPlan));
    setAmtText(request.requestAmt == null ? "" : String(request.requestAmt));
    setEditing(true);
  }
  const hours = parseQuoteNumber(hoursText);
  const amt = parseQuoteNumber(amtText);
  function saveQuote() {
    if (hours === undefined || amt === undefined) return;
    const patch = {};
    if (hours !== request.qtyPlan) patch.qtyPlan = hours;
    if (amt !== request.requestAmt) patch.requestAmt = amt;
    if (Object.keys(patch).length === 0) { setEditing(false); return; }
    setSaving(true);
    Promise.resolve(onUpdate?.(request.id, patch)).then(() => onRefresh?.()).finally(() => { setSaving(false); setEditing(false); });
  }
  const fmt = (n, suffix) => (n == null ? "—" : `${n} ${suffix}`);

  return (
    <>
      <div style={{ height: 1, background: C.mercury }} />
      <div style={{ fontSize: 11, color: C.iron, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em", display: "flex", alignItems: "center", gap: 6 }}>
        {meta ? <span className="material-icons" style={{ fontSize: 14, color: meta.color }}>{meta.icon}</span> : null}
        {t("billing.quote")}
      </div>
      {isQuoteStatus && !hasQuote ? (
        <div style={{ fontSize: 12, color: C.iron, fontStyle: "italic" }}>{t("billing.waiting_quote")}</div>
      ) : null}
      {editing ? (
        <>
          <QuoteInput label={t("billing.hours")} text={hoursText} unit="h" onChange={setHoursText} />
          <QuoteInput label={t("billing.amount")} text={amtText} unit="€" onChange={setAmtText} />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button kind="outline" size="sm" disabled={saving} onClick={() => setEditing(false)}>{t("billing.cancel")}</Button>
            <Button kind="primary" size="sm" disabled={saving || hours === undefined || amt === undefined} onClick={saveQuote}>{t("billing.save_quote")}</Button>
          </div>
        </>
      ) : (
        <>
          <PanelRow label={t("billing.hours")}><span style={{ fontSize: 12, color: C.fg }}>{fmt(request.qtyPlan, "h")}</span></PanelRow>
          <PanelRow label={t("billing.amount")}><span style={{ fontSize: 12, color: C.fg }}>{request.requestAmt == null ? "—" : `€ ${request.requestAmt}`}</span></PanelRow>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button kind="outline" size="sm" icon="edit" onClick={startEdit}>{t("billing.edit_quote")}</Button>
          </div>
        </>
      )}
    </>
  );
}

function PanelRow({ label, children }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <span
        style={{
          fontSize: 11, color: C.iron, width: 100, flex: "none",
          textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 500,
        }}
      >
        {label}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
}

export function DetailPanel({ request, role, me, showPriority, onUpdate, onRefresh }) {
  const reporter = useUser(request.reporterId);
  const assignee = useUser(request.assigneeId);
  const contact = useUser(request.contactId);
  const ResolutionsSection = useExtensionSlot('afdc.requestResolutionsSection');
  // afsp.afts's "My time" (the agent's own timesheet lines on this request) - agent-only.
  const TimeSection = useExtensionSlot('afts.requestTimeSection');

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, fontSize: 12 }}>
      <PanelRow label="Status">
        <StatusChip id={request.status} size="sm" filled readOnly />
      </PanelRow>

      {showPriority && (
        <PanelRow label={t("req.detail.priority")}>
          <PriorityChip id={request.priority} size="sm" readOnly />
        </PanelRow>
      )}

      <PanelRow label={t("req.detail.module")}>
        <ModuleChip id={request.module} size="sm" readOnly />
      </PanelRow>

      <PanelRow label={t("req.filter.type")}>
        <TypeChip id={request.type} size="sm" readOnly />
      </PanelRow>

      {role !== "customer" && (
        <PanelRow label={t("req.detail.cluster")}>
          <ClusterChip name={request.cluster?.name} size="sm" />
        </PanelRow>
      )}

      <QuoteSection request={request} role={role} me={me} onUpdate={onUpdate} onRefresh={onRefresh} />

      <div style={{ height: 1, background: C.mercury }} />

      {request.bpName && (
        <PanelRow label={t("req.filter.bpartner")}>
          <span style={{ fontSize: 12, color: C.fg }}>{request.bpName}</span>
        </PanelRow>
      )}

      {request.contactId && (
        <PanelRow label={t("req.detail.contact")}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
            <Avatar user={request.contactId} size={20} />
            <span style={{ fontSize: 12, color: C.fg, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{contact?.name ?? request.contactName ?? request.contactId}</span>
          </div>
        </PanelRow>
      )}

      <PanelRow label={t("req.detail.created_by")}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <Avatar user={request.reporterId} size={20} />
          <span style={{ fontSize: 12, color: C.fg, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{reporter?.name ?? request.reporterId}</span>
        </div>
      </PanelRow>

      <PanelRow label={t("req.detail.asm")}>
        {request.assigneeId ? (
          <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
            <Avatar user={request.assigneeId} size={20} />
            <span style={{ fontSize: 12, color: C.fg, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {request.assigneeId === me?.id ? "You" : (assignee?.name ?? request.assigneeId)}
            </span>
          </div>
        ) : (
          <span style={{ fontSize: 12, color: C.silver, fontStyle: "italic" }}>Unassigned</span>
        )}
      </PanelRow>

      <div style={{ height: 1, background: C.mercury }} />

      <PanelRow label={t("req.col.created")}>
        <span style={{ fontSize: 12, color: C.fg }}>{dateLong(request.createdAt)}</span>
      </PanelRow>

      <PanelRow label={t("req.col.updated")}>
        <span style={{ fontSize: 12, color: C.fg }} title={dateTimeFull(request.updatedAt)}>{relTime(request.updatedAt)}</span>
      </PanelRow>

      <div style={{ height: 1, background: C.mercury }} />

      <div style={{ fontSize: 11, color: C.iron, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em" }}>
        Progress
      </div>

      <ProgressBar value={request.progress} color={request.progress >= 90 ? C.patina : C.liquid} height={6} />

      <div style={{ fontSize: 11, color: C.iron, textAlign: "right" }}>{request.progress}%</div>

      <div style={{ height: 1, background: C.mercury }} />

      <WatchersSection requestId={request.id} me={me} />

      {TimeSection && isAgentRole(role) && (
        <>
          <div style={{ height: 1, background: C.mercury }} />
          <TimeSection request={request} />
        </>
      )}

      {ResolutionsSection && (
        <>
          <div style={{ height: 1, background: C.mercury }} />
          <ResolutionsSection requestId={request.id} role={role} />
        </>
      )}
    </div>
  );
}